import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button,
  IconButton,
  Snackbar,
  Alert,
  useTheme
} from '@mui/material';
import { styled } from '@mui/system';
import CloseIcon from '@mui/icons-material/Close';
import { supabase } from '../supabaseClient';

// Styled components
const BadgeContainer = styled(Box)(({ theme, unlocked }) => ({
  position: 'relative',
  width: 80,
  height: 80,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: unlocked 
    ? theme.palette.mode === 'dark' 
      ? 'rgba(138, 43, 226, 0.2)' 
      : 'rgba(138, 43, 226, 0.1)'
    : theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.1)' 
      : 'rgba(0, 0, 0, 0.1)',
  border: `2px solid ${unlocked 
    ? theme.palette.primary.main 
    : theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.2)' 
      : 'rgba(0, 0, 0, 0.2)'}`,
  boxShadow: unlocked 
    ? `0 0 20px ${theme.palette.primary.main}40` 
    : 'none',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  opacity: unlocked ? 1 : 0.6,
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: unlocked 
      ? `0 0 30px ${theme.palette.primary.main}60` 
      : 'none',
  },
  '&::before': unlocked ? {
    content: '""',
    position: 'absolute',
    top: -5,
    right: -5,
    width: 20,
    height: 20,
    borderRadius: '50%',
    backgroundColor: theme.palette.success.main,
    border: `2px solid ${theme.palette.mode === 'dark' ? '#1A1A25' : '#FFFFFF'}`,
    zIndex: 1
  } : {}
}));

const BadgeIcon = styled(Box)(({ theme, unlocked }) => ({
  fontSize: 32,
  color: unlocked 
    ? theme.palette.primary.main 
    : theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.3)' 
      : 'rgba(0, 0, 0, 0.3)',
  filter: unlocked 
    ? `drop-shadow(0 0 10px ${theme.palette.primary.main}60)` 
    : 'none',
}));

// Achievement badges data
const achievementBadges = {
  first_render: {
    id: 'first_render',
    title: 'İlk Render',
    description: 'İlk görselleştirmenizi oluşturdunuz!',
    icon: '🎨',
    points: 10
  },
  style_master: {
    id: 'style_master',
    title: 'Stil Ustası',
    description: 'En az 3 farklı stil kullandınız.',
    icon: '🎭',
    points: 20
  },
  prompt_expert: {
    id: 'prompt_expert',
    title: 'İstek Uzmanı',
    description: '10 farklı istek yazdınız.',
    icon: '✍️',
    points: 30
  },
  sharing_pro: {
    id: 'sharing_pro',
    title: 'Paylaşım Uzmanı',
    description: 'İlk görselinizi paylaştınız.',
    icon: '🔗',
    points: 15
  },
  property_creator: {
    id: 'property_creator',
    title: 'Mülk Oluşturucu',
    description: 'İlk mülkünüzü oluşturdunuz.',
    icon: '🏠',
    points: 25
  },
  workflow_explorer: {
    id: 'workflow_explorer',
    title: 'İş Akışı Kaşifi',
    description: 'Tüm iş akışlarını denediniz.',
    icon: '🔍',
    points: 40
  },
  feedback_contributor: {
    id: 'feedback_contributor',
    title: 'Geri Bildirim Katkıcısı',
    description: 'İlk geri bildiriminizi gönderdiniz.',
    icon: '📝',
    points: 15
  },
  masking_master: {
    id: 'masking_master',
    title: 'Maskeleme Ustası',
    description: 'Maskeleme aracını kullandınız.',
    icon: '🎭',
    points: 20
  }
};

const AchievementBadge = ({ badgeId, onUnlock }) => {
  const theme = useTheme();
  const [unlocked, setUnlocked] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [newlyUnlocked, setNewlyUnlocked] = useState(false);
  
  const badge = achievementBadges[badgeId];
  
  useEffect(() => {
    const checkBadgeStatus = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (user) {
          const { data } = await supabase
            .from('user_achievements')
            .select('*')
            .eq('user_id', user.id)
            .eq('badge_id', badgeId)
            .single();
            
          setUnlocked(!!data);
        }
      } catch (error) {
        console.error('Error checking badge status:', error);
      }
    };
    
    checkBadgeStatus();
  }, [badgeId]);
  
  const handleBadgeClick = () => {
    setDialogOpen(true);
  };
  
  const handleDialogClose = () => {
    setDialogOpen(false);
  };
  
  const unlockBadge = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        // Check if already unlocked
        const { data: existingBadge } = await supabase
          .from('user_achievements')
          .select('*')
          .eq('user_id', user.id)
          .eq('badge_id', badgeId)
          .single();
          
        if (!existingBadge) {
          // Add badge to user achievements
          await supabase
            .from('user_achievements')
            .insert([{
              user_id: user.id,
              badge_id: badgeId,
              unlocked_at: new Date().toISOString(),
              points: badge.points
            }]);
            
          // Update user points
          const { data: profile } = await supabase
            .from('profiles')
            .select('achievement_points')
            .eq('id', user.id)
            .single();
            
          const currentPoints = profile?.achievement_points || 0;
          
          await supabase
            .from('profiles')
            .update({
              achievement_points: currentPoints + badge.points
            })
            .eq('id', user.id);
            
          setUnlocked(true);
          setNewlyUnlocked(true);
          setSnackbarOpen(true);
          
          if (onUnlock) {
            onUnlock(badgeId, badge.points);
          }
        }
      }
    } catch (error) {
      console.error('Error unlocking badge:', error);
    }
  };
  
  return (
    <>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        gap: 1
      }}>
        <BadgeContainer unlocked={unlocked} onClick={handleBadgeClick}>
          <BadgeIcon unlocked={unlocked}>
            {badge.icon}
          </BadgeIcon>
        </BadgeContainer>
        
        <Typography 
          variant="caption" 
          sx={{ 
            textAlign: 'center',
            fontWeight: unlocked ? 600 : 400,
            color: unlocked 
              ? theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'
              : theme.palette.text.secondary,
            opacity: unlocked ? 1 : 0.7
          }}
        >
          {badge.title}
        </Typography>
      </Box>
      
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 3,
            backgroundColor: theme.palette.mode === 'dark' 
              ? 'rgba(26, 26, 37, 0.95)' 
              : '#FFFFFF',
            backgroundImage: theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, rgba(138, 43, 226, 0.05) 0%, rgba(26, 26, 37, 0) 100%)'
              : 'none',
            boxShadow: theme.palette.mode === 'dark' 
              ? '0 8px 32px rgba(0, 0, 0, 0.3)' 
              : '0 8px 32px rgba(0, 0, 0, 0.1)',
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 1
        }}>
          <Typography variant="h6">
            {badge.title}
          </Typography>
          <IconButton edge="end" color="inherit" onClick={handleDialogClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            gap: 2,
            py: 2
          }}>
            <BadgeContainer unlocked={unlocked} sx={{ width: 120, height: 120 }}>
              <BadgeIcon unlocked={unlocked} sx={{ fontSize: 64 }}>
                {badge.icon}
              </BadgeIcon>
            </BadgeContainer>
            
            <Typography variant="body1" sx={{ textAlign: 'center' }}>
              {badge.description}
            </Typography>
            
            <Typography 
              variant="subtitle1" 
              sx={{ 
                color: theme.palette.primary.main,
                fontWeight: 600
              }}
            >
              {badge.points} Puan
            </Typography>
            
            {!unlocked && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                Bu rozeti kazanmak için gerekli görevi tamamlayın.
              </Typography>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleDialogClose}
            sx={{ 
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Kapat
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity="success"
          sx={{ 
            width: '100%',
            backgroundColor: theme.palette.mode === 'dark' 
              ? 'rgba(46, 125, 50, 0.9)' 
              : 'rgba(237, 247, 237, 0.9)',
            color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#1B5E20',
            border: '1px solid',
            borderColor: theme.palette.mode === 'dark' 
              ? 'rgba(76, 175, 80, 0.5)' 
              : 'rgba(76, 175, 80, 0.3)',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            backdropFilter: 'blur(8px)'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ fontSize: 24 }}>{badge.icon}</Box>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Yeni Rozet Kazandınız: {badge.title}
              </Typography>
              <Typography variant="body2">
                +{badge.points} puan kazandınız!
              </Typography>
            </Box>
          </Box>
        </Alert>
      </Snackbar>
    </>
  );
};

// Achievement gallery component
export const AchievementGallery = () => {
  const theme = useTheme();
  const [userAchievements, setUserAchievements] = useState([]);
  const [totalPoints, setTotalPoints] = useState(0);
  
  useEffect(() => {
    const fetchUserAchievements = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (user) {
          const { data: achievements } = await supabase
            .from('user_achievements')
            .select('*')
            .eq('user_id', user.id);
            
          const { data: profile } = await supabase
            .from('profiles')
            .select('achievement_points')
            .eq('id', user.id)
            .single();
            
          setUserAchievements(achievements || []);
          setTotalPoints(profile?.achievement_points || 0);
        }
      } catch (error) {
        console.error('Error fetching user achievements:', error);
      }
    };
    
    fetchUserAchievements();
  }, []);
  
  const handleBadgeUnlock = (badgeId, points) => {
    setUserAchievements(prev => [...prev, { badge_id: badgeId }]);
    setTotalPoints(prev => prev + points);
  };
  
  return (
    <Box sx={{ py: 4 }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 4
      }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Başarılarım
        </Typography>
        
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          backgroundColor: theme.palette.mode === 'dark' 
            ? 'rgba(138, 43, 226, 0.1)' 
            : 'rgba(138, 43, 226, 0.05)',
          borderRadius: 2,
          padding: '4px 12px',
          border: `1px solid ${theme.palette.primary.main}30`
        }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Toplam Puan:
          </Typography>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              fontWeight: 700,
              color: theme.palette.primary.main
            }}
          >
            {totalPoints}
          </Typography>
        </Box>
      </Box>
      
      <Box sx={{ 
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(2, 1fr)',
          sm: 'repeat(4, 1fr)',
          md: 'repeat(4, 1fr)'
        },
        gap: 3
      }}>
        {Object.keys(achievementBadges).map(badgeId => {
          const isUnlocked = userAchievements.some(a => a.badge_id === badgeId);
          
          return (
            <AchievementBadge 
              key={badgeId} 
              badgeId={badgeId}
              onUnlock={handleBadgeUnlock}
            />
          );
        })}
      </Box>
    </Box>
  );
};

export default AchievementBadge;
